import React, { useEffect, useRef } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import DiscoveryPreview, { discoveryPreviewKind, DISCOVERY_PREVIEW_LABELS } from '@/components/Discovery/DiscoveryPreview';

import DiscoverySample, { discoverySampleKind } from '@/components/Discovery/DiscoverySample';
import { useOnScreen } from '@/hooks/useOnScreen';

export default function IdeaCard({ idea, palette, width, onPress, onLayout, featured = false, compact = false, samples = false, onSeen }) {
  const kind = discoveryPreviewKind(idea);
  const sample = samples && !compact && discoverySampleKind(idea);
  const [visibilityRef, visible] = useOnScreen({ rootMargin: '0px' });
  const reported = useRef(false);
  useEffect(() => {
    if (Platform.OS === 'web' && visible && !reported.current && onSeen) { reported.current = true; onSeen(idea, 'seen'); }
  }, [visible, idea, onSeen]);
  return <View ref={visibilityRef} onLayout={onLayout} style={[styles.card, sample && featured && styles.sampleRow, { width, backgroundColor: palette.surface, borderColor: palette.border }]}>
    {sample && <View style={featured ? styles.featuredArt : undefined}><DiscoverySample idea={idea} palette={palette} onSelect={onPress} /></View>}
    <Pressable accessibilityRole="button" accessibilityLabel={`Use idea: ${idea.title}`}
      accessibilityHint="Adds this idea to the composer for you to edit."
      onPress={() => onPress(idea)} style={({ hovered, pressed }) => [styles.main, featured && !sample && styles.horizontal,
        { backgroundColor: hovered || pressed ? palette.hover : palette.surface }]}>
      {!compact && !sample && <View style={featured ? styles.featuredArt : undefined}>
        <DiscoveryPreview idea={idea} kind={kind} height={featured ? 260 : 196} />
      </View>}
      <View style={[styles.body, compact && styles.compactBody, featured && styles.featuredBody]}>
        <View style={styles.eyebrow}><Text size="xs" color="secondary">{DISCOVERY_PREVIEW_LABELS[kind]}</Text>
          <Icon name="arrow-up-right" size={13} color={palette.icon} /></View>
        <Text weight="medium" style={[styles.title, featured && styles.featuredTitle]}>{idea.title}</Text>
        <Text size="s" color="secondary" numberOfLines={compact ? 2 : 3} style={styles.description}>{idea.description}</Text>
      </View>
    </Pressable>
  </View>;
}
const styles = StyleSheet.create({
  card: { flexGrow: 1, borderWidth: 1, borderRadius: 22, overflow: 'hidden' },
  main: { flex: 1, justifyContent: 'center' },
  sampleRow: { flexDirection: 'row-reverse', alignItems: 'stretch' },
  horizontal: { flexDirection: 'row-reverse', alignItems: 'center' },
  featuredArt: { width: '46%' },
  body: { flex: 1, padding: 24, gap: 10 },
  compactBody: { padding: 22 },
  featuredBody: { padding: 32, justifyContent: 'center' },
  eyebrow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 12 },
  title: { fontSize: 21, lineHeight: 28, letterSpacing: -0.4 },
  featuredTitle: { fontSize: 27, lineHeight: 34, letterSpacing: -0.6 },
  description: { lineHeight: 21 },
});
